import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { auth, googleProvider, signInWithPopup } from '../config/firebase';
import axios from 'axios';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, updateUser } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('Calling login...');
      const result = await login(email, password);
      console.log('Login result:', result);
      if (result && result.success) {
        toast.success('Login successful!');
        console.log('Navigating to /dashboard...');
        navigate('/dashboard');
        console.log('Navigate called');
      } else {
        toast.error((result && result.error) || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const idToken = await user.getIdToken();

      // Send the ID token to your backend
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'https://intentionalmovement-production.up.railway.app/api'}/auth/firebase`,
        { idToken }
      );

      // Backend wraps response in {success, data, message} format
      const responseData = response.data.data || response.data;

      if (responseData && responseData.token) {
        console.log('=== GOOGLE SIGN-IN RESPONSE ===');
        console.log('Full response:', response.data);
        console.log('Response data:', responseData);
        console.log('User object:', responseData.user);
        console.log('User role:', responseData.user?.role);

        // Check if user is admin
        if (responseData.user.role === 'admin') {
          console.log('✓ User is admin, updating auth context');

          // Store the token (use adminToken/adminUser keys to match authService)
          localStorage.setItem('adminToken', responseData.token);
          localStorage.setItem('adminUser', JSON.stringify(responseData.user));

          // Update AuthContext with the user data
          updateUser(responseData.user);

          toast.success('Signed in with Google!');
          console.log('Navigating to dashboard...');
          navigate('/dashboard');
        } else {
          console.log('✗ User is not admin, role:', responseData.user.role);
          toast.error(`Access denied. Admin privileges required. Your role: ${responseData.user.role || 'none'}`);
          // Sign out if not admin
          await auth.signOut();
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } else {
        console.log('✗ No token in response:', response.data);
        toast.error('Authentication failed - no token received');
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      toast.error(error.response?.data?.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-accent-50 via-white to-primary-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 border border-accent-100">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold font-heading text-accent-500">
            Intentional Movement
          </h1>
          <p className="text-gray-600 mt-2 font-body">Admin Dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2 font-body"
            >
              Email or Username
            </label>
            <input
              id="email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border border-accent-200 rounded-lg focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-colors font-body"
              placeholder="admin@example.com or username"
              autoCapitalize="none"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-2 font-body"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-2 border border-accent-200 rounded-lg focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-colors font-body"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-accent-500 text-white py-3 rounded-lg font-medium font-body hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500 font-body">Or continue with</span>
            </div>
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="mt-4 w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-medium font-body hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </button>
        </div>

        <div className="mt-6 text-center text-sm">
          <p className="text-gray-600 font-body">Admin access required</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
